import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buttonClasses } from '../../design-system/components';
import { useBookingJoin } from '../../hooks/useBookingJoin';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { cancelIncomingCallNotification } from '../../services/incoming-call-notification';
import { useActiveCohortSessionsStore } from '../../stores/active-cohort-sessions.store';
import { useAuthStore } from '../../stores/auth.store';
import { useEmpathyQueueStore } from '../../stores/empathy-queue.store';
import { useIncomingCallStore } from '../../stores/incoming-call.store';
import { withSession } from '../../stores/session-read';
import type { IncomingCall, MissedCall } from '../../types/ring';
import { dropJoinChoice, setJoinChoice } from '../../utils/callJoinIntent';
import { attachStream, cameraFailure, selectCallVideoProfile } from '../../utils/camera';
import { hapticRing } from '../../utils/haptics';
import { Z_RING } from '../../utils/layers';
import { cohortEmpathyTabPath } from '../../utils/listening';
import { CALL_ROUTE } from '../empathy/callRoutes';
import { listenerLine } from './listenerLine';

/** The server's ring window (SeekerNoShowJob::GRACE_SECONDS): the countdown's full circle. */
export const RING_WINDOW_SECONDS = 120;

/** User-facing copy, in one place so a test can read every string (D56: never "on call"). */
export const INCOMING_COPY = {
  request: (name: string) => `${name} is calling about your request`,
  match: (name: string) => `${name} is ready for your call`,
  yourRequest: 'Your request',
  bothIn: "You're both in",
  noSharedGroup: 'Listener on EmpathEase',
  waiting: (name: string) => `${name} is waiting`,
  previewCamera: 'Preview camera',
  cameraOn: 'Camera on',
  cameraOff: 'Camera off',
  micOn: 'Mic on',
  micOff: 'Mic off',
  previewLabel: 'You · only you see this',
  hint: (name: string) =>
    `${name} sees you only after you join. You can turn the camera off in the call.`,
  join: 'Join',
  joinWithCamera: 'Join with camera',
  joining: 'Joining…',
  notNow: 'Not now',
  failed: "Couldn't join the call. Try again.",
  notNowFailed: "Couldn't let them know. Try again.",
  gaveUp: (name: string) => `${name} couldn't wait any longer`,
  gaveUpTimedOut: '2 minutes passed',
  stillUp: 'Your request is still up. Anyone in your groups can still answer it.',
  seeRequest: 'See your request',
  close: 'Close',
} as const;

function clock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function stopStream(stream: MediaStream | null): void {
  try {
    stream?.getTracks().forEach((t) => t.stop());
  } catch {
    /* already stopped */
  }
}

function Avatar({
  name,
  url,
  size,
  muted = false,
}: {
  name: string;
  url: string | null;
  size: 'lg' | 'sm';
  muted?: boolean;
}) {
  const box = size === 'lg' ? 'ee-call-sheet__avatar--lg' : 'ee-call-sheet__avatar--sm';
  const fade = muted ? 'ee-call-sheet__avatar--faded' : '';
  return url ? (
    <img
      src={url}
      alt=""
      data-testid="incoming-avatar"
      className={`ee-call-sheet__avatar ${box} ${fade} object-cover`}
    />
  ) : (
    <span
      aria-hidden="true"
      data-testid="incoming-avatar"
      className={`ee-call-sheet__avatar ee-call-sheet__avatar--initial ${box} ${fade}`}
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

// Design-system Buttons in the sheet's existing violet (pages/call.css):
// the pressed toggle, Join and the camera outline keep their colours.
const TOGGLE = buttonClasses({
  variant: 'secondary',
  size: 'sm',
  className: 'ee-call-sheet__toggle flex-1',
});
/**
 * ONE SIZE FOR EVERY ACTION ON THE SHEET (David, 2026-10-27: "Join with
 * camera" read too big on a 390-wide phone). The sheet's camera toggles were
 * already the design system's `sm`; Join, Join with camera and Not now were
 * `md`, and "Join with camera" also carried a hand-set `px-3`, the one button
 * here sized outside the tokens. All of them are `sm` now: one type scale, the
 * token padding, and a 44 px target (--ee-target-min) on every one.
 */
const JOIN_BTN = buttonClasses({ size: 'sm', className: 'ee-call-sheet__join sm:flex-1' });
const JOIN_CAMERA_BTN = buttonClasses({
  variant: 'secondary',
  size: 'sm',
  className: 'ee-call-sheet__camera sm:flex-[1.4]',
});
const GHOST_BTN = buttonClasses({ variant: 'ghost', size: 'sm', className: 'sm:flex-1' });

interface Props {
  call: IncomingCall;
  /** A gate (agreement, norms) is up: the sheet waits behind it, still mounted. */
  hidden: boolean;
  /**
   * RING-PUSH: Join or Not now was tapped on this call's notification. The
   * sheet runs it through its own buttons' paths the first time it is on
   * screen (Join camera-off, as the sheet's own Join), so the outcome (the
   * call, "Couldn't join", Not now's refusals) is the sheet's.
   */
  pushAction?: 'join' | 'not_now';
}

/**
 * R11 + R14 (variant B, the approved board 2026-10-19): the seeker's
 * incoming call, on any page, the group's Empathy tab and the 1:1 waiting
 * screen included (a claim rings there too; only Join connects it).
 *
 * The trust card: the caller's face (initial fallback), "is calling about
 * your request", the request in the requester's own words, "You're both in"
 * the groups they share ("Listener on EmpathEase" when none), the caller's
 * profile line, and the two-minute countdown. It vibrates when it appears
 * (hapticRing: never a sound).
 *
 * The opt-in self-preview: the sheet never asks for the microphone, and the
 * camera only on "Preview camera" or "Join with camera" (which opens the
 * preview first rather than joining straight away, so a misclick never puts
 * anyone in a live call with their camera on). In the preview, the Mic and
 * Camera toggles are the call's own controls pre-set, and Join's label
 * follows the camera. Join hands the choice, and the running preview stream,
 * to the call (callJoinIntent), so the camera is not opened twice. Not now,
 * the window running out, or the sheet closing for any reason releases the
 * stream (camera light off). A camera the browser refuses says why and the
 * sheet stays audio.
 *
 * Join reads the seat with join={session}: the server presents the call, the
 * seat stops saying awaiting_join, and only once the store holds THIS call
 * does the sheet open the call route (a booked call goes through its own
 * join route). A read that fails, or that does not hand over the call, keeps
 * the sheet with the error (Codex #1000 P1).
 *
 * Not now declines THIS call (declineIncomingCall, decided by the server
 * under its lock, #1000 review 4); nothing is marked handled until the server
 * answers, and a failed decline keeps the sheet and says so (Codex #1000 P2).
 * For a booked call it only hides the sheet. The sheet goes by itself when
 * the server's window runs out, whatever it is doing: the window it opened
 * with (#1002 review 8: a later poll's seconds_left never re-arms it), and
 * the "couldn't wait" re-read a few seconds later is owned by the sheet, so
 * a sheet that is gone schedules nothing.
 *
 * Layer Z_RING, focus-managed like the ring sheet: focus in, Tab held
 * inside. Escape does nothing (#1002 review 9): the sheet is modal while it
 * rings, and Not now ends the caller's call, so only its button says it.
 */
export default function IncomingCallSheet({ call, hidden, pushAction }: Props) {
  const navigate = useNavigate();
  const markHandled = useIncomingCallStore((s) => s.markHandled);
  const dataSaver = useAuthStore((s) => !!s.user?.video_data_saver);
  const { join: joinBooked } = useBookingJoin();
  const [phase, setPhase] = useState<'idle' | 'joining' | 'declining'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [micOn, setMicOn] = useState(true);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [left, setLeft] = useState(() => Math.max(0, call.seconds_left));
  // The window this sheet opened with: fixed for its life (the host keys the
  // sheet by session, so a new call is a new sheet).
  const [deadline] = useState(() => Date.now() + Math.max(0, call.seconds_left) * 1000);
  const [expired, setExpired] = useState(false);
  const titleId = useId();
  const bodyId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const inFlight = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);
  const handedOff = useRef(false);
  const alive = useRef(true);
  const buzzed = useRef(false);
  const ranPushAction = useRef(false);
  const consumePushTap = useIncomingCallStore((s) => s.consumePushTap);

  // The no-show window (server-relative seconds): never shown past it, even
  // mid-join or mid-decline (#1000 review 3: a request that hangs must not
  // hold the sheet up with its buttons disabled). After it, the next poll
  // brings "{name} couldn't wait" once the server has voided the call.
  useEffect(() => {
    const tick = setInterval(() => setLeft(Math.max(0, (deadline - Date.now()) / 1000)), 1000);
    const timer = setTimeout(
      () => {
        // Off screen at once, camera released; it stays mounted (and owns
        // the re-read below) until it is marked handled.
        setExpired(true);
        if (!handedOff.current) stopStream(streamRef.current);
        streamRef.current = null;
        setStream(null);
      },
      Math.max(0, deadline - Date.now()),
    );
    return () => {
      clearInterval(tick);
      clearTimeout(timer);
    };
  }, [deadline]);

  // After the window: handled here, and the feed asked again once the server
  // has had a moment to void the call, so "{name} couldn't wait" arrives
  // without waiting for the next poll. Cleared if the sheet goes first.
  useEffect(() => {
    if (!expired) return;
    const t = setTimeout(() => {
      markHandled(call.session_id);
      void useActiveCohortSessionsStore.getState().refresh();
    }, 4000);
    return () => clearTimeout(t);
  }, [expired, call.session_id, markHandled]);

  // The camera is released whenever the sheet goes, unless Join handed it
  // to the call (camera light off on Not now, on expiry, on any close).
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (!handedOff.current) stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    attachStream(videoRef.current, stream);
  }, [stream]);

  // Buzz once, when it is actually on screen (not behind a gate). On screen,
  // the sheet IS the ringer: the call's notification goes (#1009 item 1).
  useEffect(() => {
    if (hidden) return;
    void cancelIncomingCallNotification(call.session_id);
    if (buzzed.current) return;
    buzzed.current = true;
    hapticRing();
  }, [hidden, call.session_id]);

  const releaseCamera = useCallback(() => {
    stopStream(streamRef.current);
    streamRef.current = null;
    setStream(null);
  }, []);

  /** Only on a tap: "Preview camera", the preview's Camera toggle, or "Join with camera". */
  const openCamera = async () => {
    if (cameraBusy || streamRef.current) return;
    setCameraBusy(true);
    setCameraError(null);
    try {
      const profile = selectCallVideoProfile({ dataSaver });
      const opened = await navigator.mediaDevices.getUserMedia({ video: profile.capture });
      if (!alive.current) {
        stopStream(opened);
        return;
      }
      streamRef.current = opened;
      setStream(opened);
      setPreview(true);
    } catch (err) {
      // The toggle goes back to "Preview camera", with why; Join stays audio.
      if (!alive.current) return;
      setPreview(false);
      setCameraError(cameraFailure(err).message);
    } finally {
      if (alive.current) setCameraBusy(false);
    }
  };

  /** Back to the buttons, with what went wrong (or nothing). */
  const settle = (message: string | null) => {
    setPhase('idle');
    setError(message);
  };

  const join = async (withCamera: boolean) => {
    if (inFlight.current) return;
    // "Join with camera" with no preview open shows the preview first: a
    // misclick never lands anyone in a live call with their camera on.
    if (withCamera && !streamRef.current) {
      void openCamera();
      return;
    }
    const cameraOn = withCamera && streamRef.current !== null;
    inFlight.current = true;
    setPhase('joining');
    setError(null);
    // Before the seat read: a call screen already up connects the moment the
    // store holds the call, and must find the choice waiting for it.
    setJoinChoice(
      { sessionId: call.session_id, cameraOn, micOn },
      cameraOn ? streamRef.current : null,
    );
    const takeBack = () => dropJoinChoice(call.session_id, false);
    // The call owns a camera it was handed; anything else goes with the
    // sheet (markHandled closes it, and the unmount releases it).
    const handOver = () => {
      if (cameraOn) handedOff.current = true;
      markHandled(call.session_id);
    };
    try {
      if (call.booking_id !== null) {
        const ok = await joinBooked(call.booking_id);
        if (!ok) {
          takeBack();
          settle(INCOMING_COPY.failed);
          return;
        }
        handOver();
        return;
      }
      // Read the seat first, as Join: the server presents this call (the
      // seat stops saying awaiting_join), and the 1:1 page, which may
      // already be mounted, finds it in the store.
      const read = await withSession(() =>
        useEmpathyQueueStore.getState().fetchSeekerStatus({ join: call.session_id }),
      );
      // #1000 review 2: the account changed under the read; never left in
      // "Joining…" (the user-scoped reset clears the feed, and the sheet).
      if (read.status === 'ended') {
        takeBack();
        settle(null);
        return;
      }
      const seat = useEmpathyQueueStore.getState();
      // Codex #1000 P1: a failed read is not a join; nor is a read that did
      // not hand over THIS call (it ended meanwhile: the next poll says so).
      if (
        read.status === 'failed' ||
        seat.seekerState !== 'CONNECTED' ||
        String(seat.currentSessionId) !== String(call.session_id)
      ) {
        takeBack();
        settle(INCOMING_COPY.failed);
        return;
      }
      handOver();
      void navigate(CALL_ROUTE);
    } catch {
      takeBack();
      settle(INCOMING_COPY.failed);
    } finally {
      inFlight.current = false;
    }
  };

  const notNow = async () => {
    if (inFlight.current) return;
    // The camera goes with the sheet (the unmount releases it).
    if (call.booking_id !== null) {
      markHandled(call.session_id);
      return;
    }
    inFlight.current = true;
    setPhase('declining');
    setError(null);
    try {
      // 'declined', 'reached' (live elsewhere: left alone) or 'ended': the
      // sheet has nothing more to offer for this session either way.
      await useEmpathyQueueStore.getState().declineIncomingCall(call.session_id);
      markHandled(call.session_id);
      void useActiveCohortSessionsStore.getState().refresh();
    } catch {
      settle(INCOMING_COPY.notNowFailed);
    } finally {
      inFlight.current = false;
    }
  };

  // RING-PUSH: the notification's own Join / Not now, once, when on screen,
  // through this render's handlers (the buttons' own paths).
  const handlers = useRef({ join, notNow });
  handlers.current = { join, notNow };
  useEffect(() => {
    if (hidden || expired) return;
    consumePushTap(call.session_id);
    if (!pushAction || ranPushAction.current) return;
    ranPushAction.current = true;
    if (pushAction === 'join') void handlers.current.join(false);
    else void handlers.current.notNow();
  }, [hidden, expired, pushAction, call.session_id, consumePushTap]);

  useDialogFocus(!hidden && !expired, panelRef, {
    // #1002 review 9: modal while ringing; Not now only by its button.
    onEscape: () => {},
    yieldToOtherModals: true,
  });

  if (hidden || expired) return null;

  const name = call.giver.name;
  const title = call.request_id !== null ? INCOMING_COPY.request(name) : INCOMING_COPY.match(name);
  const line = listenerLine(call.listener);
  const groups = call.shared_groups ?? [];
  const busy = phase !== 'idle';
  const cameraLive = stream !== null;
  const fraction = Math.min(1, left / RING_WINDOW_SECONDS);
  const RADIUS = 7;
  const CIRC = 2 * Math.PI * RADIUS;

  const micToggle = (extra = '') => (
    <button
      type="button"
      className={`${TOGGLE} ${extra}`}
      aria-pressed={micOn}
      onClick={() => setMicOn((on) => !on)}
      disabled={busy}
    >
      {micOn ? INCOMING_COPY.micOn : INCOMING_COPY.micOff}
    </button>
  );

  return (
    <div
      className="ee-page ee-call-sheet fixed inset-0 flex items-end sm:items-center justify-center bg-(--ee-scrim)/40 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
      style={{ zIndex: Z_RING }}
      data-testid="incoming-call-sheet"
      data-state={preview ? 'preview' : 'ringing'}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        ref={panelRef}
        tabIndex={-1}
        className="ee-card ee-card--pad-md ee-call-sheet__card w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto flex flex-col gap-3 focus:outline-none"
      >
        <div className="flex items-center gap-3">
          <Avatar name={name} url={call.giver.avatar_url} size={preview ? 'sm' : 'lg'} />
          <div className="min-w-0 flex-1 flex flex-col items-start gap-1">
            <h2
              id={titleId}
              className={`ee-call-sheet__title ${preview ? 'ee-call-sheet__title--sm' : ''}`}
            >
              {title}
            </h2>
            {line ? (
              <span data-testid="incoming-listener-line" className="ee-call-sheet__line">
                <span aria-hidden="true" className="ee-call-sheet__line-dot" />
                <span className="truncate">{line}</span>
              </span>
            ) : null}
          </div>
        </div>

        {!preview && call.request_text ? (
          <blockquote data-testid="incoming-request-quote" className="ee-call-sheet__quote">
            <small className="ee-call-sheet__quote-label">{INCOMING_COPY.yourRequest}</small>
            {`“${call.request_text}”`}
          </blockquote>
        ) : null}

        <p id={bodyId} data-testid="incoming-groups" className="ee-call-sheet__groups">
          {groups.length > 0 ? (
            <>
              {INCOMING_COPY.bothIn}
              {groups.map((g) => (
                <span key={g.id} className="ee-call-sheet__group">
                  {g.name}
                </span>
              ))}
            </>
          ) : (
            INCOMING_COPY.noSharedGroup
          )}
        </p>

        {preview ? (
          <div
            data-testid="incoming-preview"
            className="ee-call-sheet__preview relative aspect-[16/10] overflow-hidden flex items-center justify-center"
          >
            {cameraLive ? (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover -scale-x-100"
              />
            ) : (
              <span>{INCOMING_COPY.cameraOff}</span>
            )}
            <span className="ee-call-sheet__preview-label absolute left-2 top-2">
              {INCOMING_COPY.previewLabel}
            </span>
          </div>
        ) : null}

        <div className="flex gap-2">
          {preview ? (
            <>
              {micToggle()}
              <button
                type="button"
                className={TOGGLE}
                aria-pressed={cameraLive}
                onClick={() => (cameraLive ? releaseCamera() : void openCamera())}
                disabled={busy || cameraBusy}
              >
                {cameraLive ? INCOMING_COPY.cameraOn : INCOMING_COPY.cameraOff}
              </button>
            </>
          ) : (
            <>
              {micToggle('hidden sm:inline-flex')}
              <button
                type="button"
                className={TOGGLE}
                aria-pressed={false}
                onClick={() => void openCamera()}
                disabled={busy || cameraBusy}
              >
                {INCOMING_COPY.previewCamera}
              </button>
            </>
          )}
        </div>
        {cameraError ? (
          <p className="ee-call-sheet__camera-error" data-testid="incoming-camera-error">
            {cameraError}
          </p>
        ) : null}

        {error ? (
          <p className="ee-call-sheet__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="ee-call-sheet__waiting">
          <span>{INCOMING_COPY.waiting(name)}</span>
          <span className="ee-call-sheet__countdown" data-testid="incoming-countdown">
            <svg viewBox="0 0 18 18" className="w-[18px] h-[18px] -rotate-90" aria-hidden="true">
              <circle
                cx="9"
                cy="9"
                r={RADIUS}
                fill="none"
                strokeWidth="2.5"
                className="ee-call-sheet__ring-track"
              />
              <circle
                cx="9"
                cy="9"
                r={RADIUS}
                fill="none"
                strokeWidth="2.5"
                strokeLinecap="round"
                className="ee-call-sheet__ring-fill motion-safe:transition-[stroke-dashoffset] motion-safe:duration-1000 motion-safe:ease-linear"
                strokeDasharray={CIRC}
                strokeDashoffset={CIRC * (1 - fraction)}
              />
            </svg>
            {clock(left)}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row-reverse gap-2">
          <button
            type="button"
            className={JOIN_BTN}
            onClick={() => void join(preview && cameraLive)}
            disabled={busy}
          >
            {phase === 'joining' ? (
              <>
                <span className="loading loading-spinner loading-sm" aria-hidden="true" />
                {INCOMING_COPY.joining}
              </>
            ) : preview && cameraLive ? (
              INCOMING_COPY.joinWithCamera
            ) : (
              INCOMING_COPY.join
            )}
          </button>
          {!preview ? (
            <button
              type="button"
              className={JOIN_CAMERA_BTN}
              onClick={() => void join(true)}
              disabled={busy || cameraBusy}
            >
              {INCOMING_COPY.joinWithCamera}
            </button>
          ) : null}
          <button type="button" className={GHOST_BTN} onClick={() => void notNow()} disabled={busy}>
            {INCOMING_COPY.notNow}
          </button>
        </div>
        {preview ? <p className="ee-call-sheet__hint">{INCOMING_COPY.hint(name)}</p> : null}
      </div>
    </div>
  );
}

/**
 * R14 item 3: the caller gave up (the two minutes ran out, or they hung up
 * first) on a call the requester never joined: a small closing card instead
 * of a sheet that vanished mid-thought. From the poll's one-shot
 * `missed_call`, latched in the feed store until Close.
 */
export function CallerGaveUpSheet({
  missed,
  hidden,
  onClose,
}: {
  missed: MissedCall;
  hidden: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  useDialogFocus(!hidden, panelRef, { onEscape: onClose, yieldToOtherModals: true });

  if (hidden) return null;

  const name = missed.giver.name;
  const open = missed.request?.open === true;
  const cohortId = missed.request?.cohort_id ?? null;

  return (
    <div
      className="ee-page ee-call-sheet fixed inset-0 flex items-end sm:items-center justify-center bg-(--ee-scrim)/40 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
      style={{ zIndex: Z_RING }}
      data-testid="caller-gave-up-sheet"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        tabIndex={-1}
        className="ee-card ee-card--pad-md ee-call-sheet__card w-full max-w-md flex flex-col gap-3 focus:outline-none"
      >
        <div className="flex items-center gap-3">
          <Avatar name={name} url={missed.giver.avatar_url} size="lg" muted />
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="ee-call-sheet__title">
              {INCOMING_COPY.gaveUp(name)}
            </h2>
            {missed.reason === 'timed_out' ? (
              <span className="ee-call-sheet__sub">{INCOMING_COPY.gaveUpTimedOut}</span>
            ) : null}
          </div>
        </div>
        {open ? <p className="ee-call-sheet__body">{INCOMING_COPY.stillUp}</p> : null}
        <div className="flex flex-col sm:flex-row-reverse gap-2 mt-1">
          {open && cohortId !== null ? (
            <button
              type="button"
              className={JOIN_BTN}
              onClick={() => {
                onClose();
                void navigate(cohortEmpathyTabPath(cohortId));
              }}
            >
              {INCOMING_COPY.seeRequest}
            </button>
          ) : null}
          <button type="button" className={GHOST_BTN} onClick={onClose}>
            {INCOMING_COPY.close}
          </button>
        </div>
      </div>
    </div>
  );
}
